import { createContext, useContext } from "react";
import { motion, useTransform } from "framer-motion";

export const C = {
  em: "#087F5B",
  deep: "#075B43",
  mint: "#E6F4ED",
  leaf: "#CFE9DC",
  dot: "#8FE3C2",
  ink: "#17211D",
  ink2: "#24302B",
  muted: "#68756E",
  line: "#E2E8E4",
  soft: "#F6F9F7",
  grey: "#D5DDD8",
  skin: "#8A5A3C",
  skin2: "#D9A47E",
  hair: "#1E1A17",
  pants: "#2A332F",
  hoodie: "#3A4540",
};

export const ProgressCtx = createContext(null);
export const useP = () => useContext(ProgressCtx);

// Maps a cue (0..1.6) to a scroll window so every part assembles between ~20% and ~75% of the scene.
const win = (d, len = 0.14) => {
  const s = 0.2 + d * 0.32;
  return [s, s + len];
};

export const useSeg = (d, from, to, len) => {
  const p = useP();
  const [a, b] = win(d, len);
  return useTransform(p, [a, b], [from, to], { clamp: true });
};

export const Rise = ({ d = 0, dy = 26, children }) => {
  const opacity = useSeg(d, 0, 1);
  const y = useSeg(d, dy, 0);
  return <motion.g style={{ opacity, y }}>{children}</motion.g>;
};

export const Slide = ({ d = 0, x: fx = 0, y: fy = 0, len, children }) => {
  const opacity = useSeg(d, 0, 1, len ? len * 0.6 : 0.1);
  const x = useSeg(d, fx, 0, len);
  const y = useSeg(d, fy, 0, len);
  return <motion.g style={{ opacity, x, y }}>{children}</motion.g>;
};

export const Pop = ({ d = 0, children }) => {
  const opacity = useSeg(d, 0, 1, 0.08);
  const scale = useSeg(d, 0.4, 1, 0.1);
  return (
    <motion.g style={{ opacity, scale }}>
      {children}
    </motion.g>
  );
};

export const Draw = ({ cue = 0, ...props }) => {
  const pathLength = useSeg(cue, 0, 1, 0.18);
  const opacity = useSeg(cue, 0, 1, 0.04);
  return <motion.path style={{ pathLength, opacity }} {...props} />;
};

export const Art = ({ children, label }) => (
  <svg viewBox="0 0 640 480" role="img" aria-label={label} className="block h-auto w-full">
    {children}
  </svg>
);

export const Browser = ({ x, y, w, h, children, fill = "#FFFFFF" }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width={w} height={h} rx="14" fill={fill} stroke={C.line} strokeWidth="2" />
    <path d={`M0 14a14 14 0 0 1 14-14h${w - 28}a14 14 0 0 1 14 14v14H0z`} fill={C.soft} />
    <circle cx="16" cy="14" r="3.5" fill={C.grey} />
    <circle cx="28" cy="14" r="3.5" fill={C.grey} />
    <circle cx="40" cy="14" r="3.5" fill={C.em} />
    <rect x={w / 2 - 44} y="8" width="88" height="12" rx="6" fill="#FFFFFF" />
    {children}
  </g>
);

export const Mark = ({ x, y, s = 16 }) => (
  <g transform={`translate(${x} ${y}) scale(${s / 48})`}>
    <rect width="48" height="48" rx="12" fill={C.em} />
    <path d="M17 36V12.5h14M17 23.5h11" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="32" cy="34" r="3" fill={C.dot} />
  </g>
);

export const Plant = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M0 -40 C-22 -52 -30 -78 -18 -92 C-6 -76 -2 -60 0 -40Z" fill={C.em} />
    <path d="M0 -40 C18 -60 34 -64 40 -86 C20 -86 6 -70 0 -40Z" fill={C.deep} />
    <path d="M0 -40 C-4 -66 4 -94 14 -104 C18 -84 10 -60 0 -40Z" fill="#0E9468" />
    <path d="M-16 -40 H16 L12 0 H-12Z" fill={C.ink2} />
  </g>
);

const Arm = ({ d, sleeve, hand, skin }) => (
  <g>
    <path d={d} stroke={skin} strokeWidth="11" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d={sleeve} stroke="#FFFFFF" strokeWidth="16" fill="none" strokeLinecap="round" />
    <circle cx={hand[0]} cy={hand[1]} r="7" fill={skin} />
  </g>
);

const POSES = {
  idea: [
    { d: "M-31 -204 Q-42 -168 -38 -126", sleeve: "M-31 -204 Q-35 -192 -36 -184", hand: [-38, -124] },
    { d: "M31 -204 Q58 -236 46 -284", sleeve: "M31 -204 Q38 -212 43 -220", hand: [46, -288] },
  ],
  confident: [
    { d: "M-31 -204 Q-60 -168 -26 -134", sleeve: "M-31 -204 Q-38 -194 -42 -186", hand: [-24, -134] },
    { d: "M31 -204 Q60 -168 26 -134", sleeve: "M31 -204 Q38 -194 42 -186", hand: [24, -134] },
  ],
};

const Head = () => (
  <g>
    <circle cx="0" cy="-250" r="22" fill={C.skin} />
    <path d="M-23 -248 Q-26 -277 0 -277 Q26 -277 23 -248 Q15 -266 -2 -264 Q-16 -262 -23 -248Z" fill={C.hair} />
    <circle cx="5" cy="-283" r="11" fill={C.hair} />
    <circle cx="-7" cy="-248" r="2.2" fill={C.hair} />
    <circle cx="7" cy="-248" r="2.2" fill={C.hair} />
    <path d="M-5 -238 Q0 -234 5 -238" stroke={C.hair} strokeWidth="2" fill="none" strokeLinecap="round" />
  </g>
);

export const Owner = ({ x, y, s = 1, pose = "idea", armRot, headTilt, legSwing }) => {
  const [left, right] = POSES[pose];
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx="0" cy="2" rx="48" ry="7" fill={C.ink} opacity="0.08" />
      <motion.g style={{ rotate: legSwing, originX: 0.5, originY: 0 }}>
        <rect x="-21" y="-112" width="15" height="108" rx="7.5" fill={C.pants} />
        <path d="M-30 0 V-3 a5 5 0 0 1 5 -5 H-6 V0Z" fill="#FFFFFF" stroke={C.grey} strokeWidth="2" />
      </motion.g>
      <rect x="6" y="-112" width="15" height="108" rx="7.5" fill={C.pants} />
      <path d="M6 0 V-8 H25 a5 5 0 0 1 5 5 V0Z" fill="#FFFFFF" stroke={C.grey} strokeWidth="2" />
      <path d="M-33 -198 Q-36 -212 -18 -216 H18 Q36 -212 33 -198 L29 -104 H-29Z" fill="#FFFFFF" stroke={C.line} strokeWidth="2" />
      <path d="M-22 -184 H22 L28 -98 Q0 -90 -28 -98Z" fill={C.em} />
      <rect x="-12" y="-150" width="24" height="16" rx="3" fill={C.deep} />
      <path d="M-20 -184 L-13 -214 M20 -184 L13 -214" stroke={C.em} strokeWidth="4" strokeLinecap="round" />
      <rect x="-7" y="-232" width="14" height="18" rx="5" fill={C.skin} />
      <motion.g style={{ rotate: headTilt, originX: 0.5, originY: 1 }}>
        <Head />
      </motion.g>
      <Arm {...left} skin={C.skin} />
      <motion.g style={{ rotate: armRot, originX: pose === "idea" ? 0 : 0.5, originY: pose === "idea" ? 1 : 0 }}>
        <Arm {...right} skin={C.skin} />
      </motion.g>
    </g>
  );
};

export const Designer = ({ x, y, bob, armL, armR }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect x="-50" y="-128" width="100" height="120" rx="20" fill={C.ink2} />
    <path d="M-46 0 L-42 -80 Q-40 -98 -18 -102 H18 Q40 -98 42 -80 L46 0Z" fill={C.hoodie} />
    <path d="M-18 -102 Q0 -86 18 -102" stroke="#2E3833" strokeWidth="5" fill="none" strokeLinecap="round" />
    <rect x="-7" y="-118" width="14" height="18" rx="5" fill={C.skin2} />
    <motion.g style={{ y: bob }}>
      <circle cx="0" cy="-138" r="21" fill={C.skin2} />
      <path d="M-21 -142 Q-23 -164 0 -164 Q23 -164 21 -142 Q17 -154 0 -154 Q-15 -154 -21 -142Z" fill={C.hair} />
      <path d="M-19 -136 Q-18 -112 0 -110 Q18 -112 19 -136 Q12 -124 0 -124 Q-12 -124 -19 -136Z" fill={C.hair} />
      <circle cx="-7" cy="-141" r="2" fill={C.hair} />
      <circle cx="7" cy="-141" r="2" fill={C.hair} />
    </motion.g>
    <motion.g style={{ y: armL }}>
      <path d="M-38 -80 Q-54 -32 -22 -12" stroke={C.hoodie} strokeWidth="15" fill="none" strokeLinecap="round" />
      <circle cx="-20" cy="-10" r="7" fill={C.skin2} />
    </motion.g>
    <motion.g style={{ y: armR }}>
      <path d="M38 -80 Q54 -32 22 -12" stroke={C.hoodie} strokeWidth="15" fill="none" strokeLinecap="round" />
      <circle cx="20" cy="-10" r="7" fill={C.skin2} />
    </motion.g>
    <rect x="-56" y="-66" width="112" height="64" rx="7" fill="#E9EEEB" stroke={C.grey} strokeWidth="2" />
    <Mark x={-9} y={-43} s={18} />
    <rect x="-66" y="-3" width="132" height="5" rx="2.5" fill={C.grey} />
  </g>
);
